/**
 * Single-product configurator state (candle, diffuser, bottle/jar, cosmetic, drinkware).
 * Pure reducer + selectors; UI components only dispatch actions. The same
 * shape feeds the RFQ builder, so pricing or saved drafts plug in here.
 */
import type { ArtworkPlacement, Coverage, InkSetup, MaterialId, ProductConfiguration } from '@shared/rfq';
import { dpiLevel, effectiveDpi, type ProcessedArtwork } from '../artwork/processArtwork';
import { analysePlacement, boundingBox, defaultPlacement, fullWrapPlacement, suggestCoverage, wrapU } from './geometry';
import { circumference, defaultContainer, getContainer, recommendedInk, type SingleProductKind } from './model';

export const STEP_IDS = ['container', 'glass', 'upload', 'editor', 'print', 'quantity', 'rfq'] as const;
export type StepId = (typeof STEP_IDS)[number];

export type UploadStatus = 'idle' | 'uploading' | 'done' | 'failed' | 'offline';

export interface ProductState {
  product: SingleProductKind;
  step: number;
  maxStep: number;
  containerId: string;
  material: MaterialId;
  artwork: ProcessedArtwork | null;
  upload: { status: UploadStatus; id: string | null };
  designHelp: boolean;
  placement: ArtworkPlacement | null;
  view: 'front' | 'flat';
  viewAngleDeg: number;
  ink: InkSetup;
  inkTouched: boolean;
  coverage: Coverage;
  coverageTouched: boolean;
  quantity: number;
  skuCount: number;
  targetDate: string;
}

export function initialState(product: SingleProductKind): ProductState {
  const c = defaultContainer(product);
  return {
    product,
    step: 0,
    maxStep: 0,
    containerId: c.id,
    material: c.defaultMaterial,
    artwork: null,
    upload: { status: 'idle', id: null },
    designHelp: false,
    placement: null,
    view: 'front',
    viewAngleDeg: 180,
    ink: recommendedInk(c.defaultMaterial),
    inkTouched: false,
    coverage: 'partial',
    coverageTouched: false,
    quantity: product === 'drinkware' ? 250 : 500,
    skuCount: 1,
    targetDate: '',
  };
}

export type ProductAction =
  | { type: 'goto'; step: number }
  | { type: 'container'; id: string }
  | { type: 'material'; material: MaterialId }
  | { type: 'artwork'; artwork: ProcessedArtwork | null }
  | { type: 'upload'; status: UploadStatus; id?: string | null }
  | { type: 'designHelp'; value: boolean }
  | { type: 'placement'; placement: ArtworkPlacement }
  | { type: 'center' }
  | { type: 'resetPlacement' }
  | { type: 'fitHeight' }
  | { type: 'fullWrap' }
  | { type: 'view'; view: 'front' | 'flat' }
  | { type: 'viewAngle'; deg: number }
  | { type: 'ink'; ink: InkSetup }
  | { type: 'coverage'; coverage: Coverage }
  | { type: 'quantity'; quantity: number }
  | { type: 'skuCount'; skuCount: number }
  | { type: 'targetDate'; date: string }
  | { type: 'reset' };

function frontU(s: Pick<ProductState, 'containerId' | 'viewAngleDeg'>): number {
  const c = getContainer(s.containerId);
  return wrapU(((s.viewAngleDeg * Math.PI) / 180) * (c.diameterMm / 2), circumference(c));
}

function autoCoverage(s: ProductState): ProductState {
  if (s.coverageTouched || !s.placement) return s;
  const c = getContainer(s.containerId);
  const C = circumference(c);
  const r = analysePlacement(s.placement, C, c.print.heightMm, c.noPrint);
  return { ...s, coverage: suggestCoverage(r, boundingBox(s.placement), C, c.print.heightMm) };
}

export function productReducer(s: ProductState, a: ProductAction): ProductState {
  switch (a.type) {
    case 'goto': {
      const step = Math.max(0, Math.min(STEP_IDS.length - 1, a.step));
      return { ...s, step, maxStep: Math.max(s.maxStep, step) };
    }
    case 'container': {
      const c = getContainer(a.id);
      const material = c.materials.includes(s.material) ? s.material : c.defaultMaterial;
      const next = { ...s, containerId: a.id, material, ink: s.inkTouched ? s.ink : recommendedInk(material) };
      if (s.artwork) next.placement = defaultPlacement(c, s.artwork.aspect, frontU(next));
      return autoCoverage(next);
    }
    case 'material':
      return { ...s, material: a.material, ink: s.inkTouched ? s.ink : recommendedInk(a.material) };
    case 'artwork': {
      if (!a.artwork) return { ...s, artwork: null, placement: null, upload: { status: 'idle', id: null } };
      const c = getContainer(s.containerId);
      return autoCoverage({
        ...s,
        artwork: a.artwork,
        designHelp: false,
        upload: { status: 'idle', id: null },
        placement: defaultPlacement(c, a.artwork.aspect, frontU(s)),
      });
    }
    case 'upload':
      return { ...s, upload: { status: a.status, id: a.id ?? s.upload.id } };
    case 'designHelp':
      return { ...s, designHelp: a.value };
    case 'placement':
      return autoCoverage({ ...s, placement: a.placement });
    case 'center': {
      if (!s.placement) return s;
      const c = getContainer(s.containerId);
      return autoCoverage({ ...s, placement: { ...s.placement, uMm: frontU(s), vMm: c.print.heightMm / 2 } });
    }
    case 'resetPlacement':
      if (!s.artwork) return s;
      return autoCoverage({ ...s, placement: defaultPlacement(getContainer(s.containerId), s.artwork.aspect, frontU(s)) });
    case 'fitHeight': {
      if (!s.placement || !s.artwork) return s;
      const H = getContainer(s.containerId).print.heightMm;
      return autoCoverage({ ...s, placement: { ...s.placement, heightMm: H, widthMm: H * s.artwork.aspect, vMm: H / 2, rotationDeg: 0 } });
    }
    case 'fullWrap':
      if (!s.artwork) return s;
      return {
        ...s,
        placement: fullWrapPlacement(getContainer(s.containerId), s.artwork.aspect, frontU(s)),
        coverage: 'full_wrap',
        coverageTouched: true,
      };
    case 'view':
      return { ...s, view: a.view };
    case 'viewAngle':
      return { ...s, viewAngleDeg: a.deg };
    case 'ink':
      return { ...s, ink: a.ink, inkTouched: true };
    case 'coverage':
      return { ...s, coverage: a.coverage, coverageTouched: true };
    case 'quantity':
      return { ...s, quantity: a.quantity };
    case 'skuCount':
      return { ...s, skuCount: a.skuCount };
    case 'targetDate':
      return { ...s, targetDate: a.date };
    case 'reset':
      return initialState(s.product);
  }
}

/** Can the user move on from a step? Returns an i18n key of the blocker, or null. */
export function stepBlocker(s: ProductState, step: StepId): 'upload' | null {
  if (step === 'upload' && !s.artwork && !s.designHelp) return 'upload';
  return null;
}

export interface Derived {
  dpi: number | null;
  dpiLevel: ReturnType<typeof dpiLevel>;
  coverageRatio: number | null;
  outsideBand: boolean;
  hitsNoPrint: boolean;
  wrapFraction: number;
  suggestedCoverage: Coverage | null;
}

export function derive(s: ProductState): Derived {
  const c = getContainer(s.containerId);
  const C = circumference(c);
  if (!s.artwork || !s.placement) {
    return { dpi: null, dpiLevel: 'vector', coverageRatio: null, outsideBand: false, hitsNoPrint: false, wrapFraction: 0, suggestedCoverage: null };
  }
  const dpi = effectiveDpi(s.artwork, s.placement.widthMm);
  const r = analysePlacement(s.placement, C, c.print.heightMm, c.noPrint);
  return {
    dpi,
    dpiLevel: dpiLevel(dpi),
    coverageRatio: r.coverageRatio,
    outsideBand: r.outsideBand,
    hitsNoPrint: r.hitsNoPrint,
    wrapFraction: r.wrapFraction,
    suggestedCoverage: suggestCoverage(r, boundingBox(s.placement), C, c.print.heightMm),
  };
}

/** Serialisable configuration for the RFQ and CRM. */
export function toConfiguration(s: ProductState, closureLabel: string | null, warnings: string[], ids: { artworkId: string | null; mockupId: string | null }): ProductConfiguration {
  const c = getContainer(s.containerId);
  const d = derive(s);
  const a = s.artwork;
  return {
    product: s.product,
    containerId: c.id,
    containerLabel: c.name,
    diameterMm: c.diameterMm,
    heightMm: c.heightMm,
    volumeMl: c.volumeMl,
    printBand: { circumferenceMm: Math.round(circumference(c) * 10) / 10, heightMm: c.print.heightMm },
    material: s.material,
    closure: closureLabel,
    ink: s.ink,
    coverage: s.coverage,
    designHelpRequested: s.designHelp,
    artwork: a
      ? {
          artworkId: ids.artworkId,
          fileName: a.fileName,
          mime: a.mime,
          sourceFormat: a.format,
          sizeBytes: a.sizeBytes,
          sha256: a.sha256,
          kind: a.kind,
          pixelWidth: a.pixelWidth,
          pixelHeight: a.pixelHeight,
          effectiveDpi: d.dpi,
          hasTransparency: a.hasTransparency,
          ...(a.pdf ? { pdfPages: a.pdf.pages } : {}),
          warnings,
        }
      : null,
    placement: s.placement,
    coverageRatio: d.coverageRatio != null ? Math.round(d.coverageRatio * 1000) / 1000 : null,
    mockupId: ids.mockupId,
  };
}

/** Fields that survive a page reload (the artwork file itself does not). */
export function persistable(s: ProductState): Partial<ProductState> {
  const { artwork: _a, upload: _u, placement: _p, step: _s, maxStep: _m, ...rest } = s;
  return rest;
}
